import { useCallback, useEffect, useState } from 'react'
import { Empty, ErrorBanner, formatUtc, useAsync } from '../../components/Async'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { ApiError, deadLetters, insights, retention, type DeadLetterBody, type RetentionSettings } from '../../lib/api'
import { PageTitle } from '../../components/PageTitle'

/**
 * The read models that can be recomputed from the event feed.
 *
 * A fixed list rather than whatever the state table happens to hold: the state row only exists once a
 * projection has been rebuilt, so deriving the buttons from it would hide the repair on exactly the
 * deployment that has never performed one.
 */
const REBUILDABLE = ['device_activity', 'detection_trend'] as const

/**
 * The two operational things an administrator actually has to do: decide how long data is kept, and
 * deal with the events the server could not store.
 *
 * Dead letters are shown open-first because an unread one is the record of a defect. Nothing is ever
 * deleted from here by hand — a replay is the only action, and it is a second attempt at the exact
 * bytes that failed, which is what makes it a verification of a fix rather than a way to make the
 * queue shorter.
 *
 * Shortening a retention period is asked about and lengthening one is not, because only one of them
 * destroys anything. These are number inputs with a Save beside them, which is the shape of a form
 * that saves a preference; what it actually does is hand the next sweep a list of things to delete,
 * and the sweep does not ask. A typo in the events box - 30 where 300 was meant - is a year of
 * evidence gone at the next run, from a screen that said "Retention saved."
 */
export function SystemPage() {
  const settings = useAsync(() => retention.get(), [])
  const projections = useAsync(() => insights.projections(), [])
  const [letters, setLetters] = useState<Awaited<ReturnType<typeof deadLetters.list>> | null>(null)
  const [openOnly, setOpenOnly] = useState(true)
  const [body, setBody] = useState<DeadLetterBody | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Whether the "this deletes things" question is open. See `shortened` for when it is asked.
  const [asking, setAsking] = useState(false)

  const rebuild = useCallback(
    async (projection: string) => {
      setBusy(true)
      setFailure(null)
      setNotice(null)
      try {
        const result = await insights.rebuild(projection)
        setNotice(`Rebuilt ${result.projection} from ${result.rows.toLocaleString()} event(s).`)
        projections.reload()
      } catch (error) {
        setFailure(error instanceof ApiError ? error.message : 'The rebuild failed.')
      } finally {
        setBusy(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )
  const [draft, setDraft] = useState<RetentionSettings | null>(null)

  const load = useCallback(async () => {
    try {
      setLetters(await deadLetters.list(openOnly))
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The dead letters could not be loaded.')
    }
  }, [openOnly])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (settings.value && draft === null) setDraft(settings.value)
  }, [settings.value, draft])

  const run = useCallback(
    async (work: () => Promise<string>) => {
      setBusy(true)
      setFailure(null)
      setNotice(null)
      try {
        setNotice(await work())
        await load()
      } catch (error) {
        setFailure(error instanceof ApiError ? error.message : 'The request failed.')
      } finally {
        setBusy(false)
      }
    },
    [load]
  )

  /**
   * The periods this save shortens, against what is stored now.
   *
   * Empty when nothing is being shortened, which is the whole test for whether to ask. Compared
   * against `settings.value` rather than against the defaults: a tenant that has never chosen is
   * living by the deployment's numbers, and dropping below one of those deletes just as much.
   */
  const shortened =
    draft && settings.value
      ? ([
          ['Captures', settings.value.artifactDays, draft.artifactDays],
          ['Events', settings.value.eventDays, draft.eventDays],
          ['Dead letters', settings.value.deadLetterDays, draft.deadLetterDays]
        ] as const).filter(([, was, now]) => Number.isFinite(now) && now < was)
      : []

  const save = () =>
    run(async () => {
      if (!draft) throw new ApiError(400, 'Nothing to save.')
      await retention.set(draft)
      settings.reload()
      return 'Retention saved. The next sweep applies it.'
    })

  const replay = (deadLetterId: string) =>
    run(async () => {
      const result = await deadLetters.replay(deadLetterId)
      setBody(null)
      return `Replayed as event ${result.eventRecordId}.`
    })

  const inspect = (deadLetterId: string) =>
    run(async () => {
      setBody(await deadLetters.get(deadLetterId))
      return 'Opened.'
    })

  return (
    <>
      <div className="page-header">
        <div>
          <PageTitle>System</PageTitle>
          <p>How long data is kept, the read models that can be repaired, and the events that could not be stored.</p>
        </div>
      </div>

      {/*
        Moved here from the Board.
        
        Rebuilding a projection is the documented repair for a count inflated by a redelivered message
        (OPERATIONS.md), and it is an operator's job rather than a tenant administrator's. The Board is
        where somebody decides what to do about their fleet; repairing the console belongs beside
        retention and the dead letters, which is the rest of this screen (D-347).
      */}
      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>Read models</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Counts are accumulated as events arrive, so a redelivered message can inflate one.
            Rebuilding recomputes it from the event feed, which is the repair.
          </p>
          <div className="toolbar" data-testid="projection-state">
            {REBUILDABLE.map((projection) => {
              const state = projections.value?.find((candidate) => candidate.projection === projection)
              return (
                <button
                  key={projection}
                  type="button"
                  onClick={() => rebuild(projection)}
                  disabled={busy}
                  data-testid={`rebuild-${projection}`}
                  title={state?.rebuiltUtc ? `Last rebuilt ${formatUtc(state.rebuiltUtc)}` : 'Never rebuilt'}
                >
                  Rebuild {projection}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      <ErrorBanner message={failure ?? settings.error} />
      {notice && (
        <div className="banner success" role="status" data-testid="system-notice">
          {notice}
        </div>
      )}

      <div className="card">
        <div className="card-body">
          <h3 style={{ marginTop: 0 }}>Retention</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Captures cannot be kept longer than the events that explain why they were taken. Events
            leave a whole month at a time, so a period is a floor rather than a deadline.
          </p>

          {/* A tenant that has never chosen is told so, rather than shown numbers indistinguishable
              from a decision somebody made. The Credential lifetime card has always done this. */}
          {settings.value && settings.value.configured === false && (
            <p className="hint" data-testid="retention-defaults">
              Nobody has chosen these yet — they are this deployment's defaults. Saving them makes them
              this organization's own.
            </p>
          )}

          {draft && (
            <div className="toolbar">
              <label className="field">
                Captures (days)
                <input
                  type="number"
                  min={1}
                  value={draft.artifactDays}
                  onChange={(event) => setDraft({ ...draft, artifactDays: Number(event.target.value) })}
                  data-testid="retention-artifact-days"
                />
              </label>
              <label className="field">
                Events (days)
                <input
                  type="number"
                  min={1}
                  value={draft.eventDays}
                  onChange={(event) => setDraft({ ...draft, eventDays: Number(event.target.value) })}
                  data-testid="retention-event-days"
                />
              </label>
              <label className="field">
                Dead letters (days)
                <input
                  type="number"
                  min={1}
                  value={draft.deadLetterDays}
                  onChange={(event) => setDraft({ ...draft, deadLetterDays: Number(event.target.value) })}
                  data-testid="retention-dead-letter-days"
                />
              </label>
              <button
                type="button"
                className="primary"
                onClick={() => (shortened.length > 0 ? setAsking(true) : save())}
                disabled={busy}
                data-testid="save-retention"
              >
                Save
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-body toolbar">
          <h3 style={{ margin: 0 }}>Dead letters</h3>
          <button
            type="button"
            className={openOnly ? 'primary' : undefined}
            onClick={() => setOpenOnly(true)}
            data-testid="dead-letters-open"
          >
            Open
          </button>
          <button
            type="button"
            className={openOnly ? undefined : 'primary'}
            onClick={() => setOpenOnly(false)}
            data-testid="dead-letters-all"
          >
            Everything
          </button>
        </div>

        <div className="table-scroll">
          <table data-testid="dead-letter-table">
            <thead>
              <tr>
                <th>Failure</th>
                <th>Stage</th>
                <th className="secondary">Recorded</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {letters?.items.map((letter) => (
                <tr key={letter.deadLetterId}>
                  <td>
                    {letter.failure}
                    <div className="muted">
                      {letter.eventType ?? 'unclassified'} · {letter.bodyBytes} bytes
                      {letter.replayCount > 0 && ` · replayed ${letter.replayCount}×`}
                    </div>
                  </td>
                  <td>
                    <span className="pill">{letter.stage}</span>
                  </td>
                  <td className="muted secondary">{formatUtc(letter.recordedUtc)}</td>
                  <td>
                    <button type="button" onClick={() => inspect(letter.deadLetterId)} disabled={busy}>
                      Inspect
                    </button>
                    {letter.replayedUtc === null && (
                      <button
                        type="button"
                        onClick={() => replay(letter.deadLetterId)}
                        disabled={busy}
                        data-testid={`replay-${letter.deadLetterId}`}
                      >
                        Replay
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {letters && letters.items.length === 0 && (
          <Empty>{openOnly ? 'Nothing failed to store.' : 'No dead letters.'}</Empty>
        )}

        {body && (
          <div className="card-body">
            <h3>The body as it arrived</h3>
            <pre className="mono" data-testid="dead-letter-body" style={{ overflowX: 'auto' }}>
              {body.body}
            </pre>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={asking}
        title="Shorten what is kept"
        confirmLabel="Save and delete on the next sweep"
        danger
        busy={busy}
        confirmPhrase="delete"
        // Closed before the request rather than after it. The server refuses a capture period longer
        // than the event period, and that refusal is rendered on the page - which a modal covers.
        onConfirm={() => {
          setAsking(false)
          void save()
        }}
        onClose={() => setAsking(false)}
        data-testid="retention-confirm"
      >
        <div className="banner warning" role="note" data-testid="retention-shortened">
          <p style={{ marginTop: 0 }}>Shortening these deletes what falls outside them, permanently:</p>
          <ul style={{ marginBottom: 0 }}>
            {shortened.map(([label, was, now]) => (
              <li key={label}>
                {label}: {was} days to {now}
              </li>
            ))}
          </ul>
        </div>

        <p>
          The next sweep applies this. Events leave a whole month at a time, so a period is a floor
          rather than a deadline — but what goes is gone, and nothing on this server puts it back.
        </p>
      </ConfirmDialog>
    </>
  )
}
